import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { DiscountFreeRangeRow } from '../../../../../core/models/parking/discount-free-range.model';
import { MapperDiscountFreeRangeService } from '../../../../../core/mappers/parking/mapper-discount-free-range.service';
import { ApiDiscountFreeRangeService } from '../../../../../core/services/api/parking/api-discount-free-range.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalDiscountFreeRangeComponent } from '../../../modals/parking/modal-discount-free-range/modal-discount-free-range.component';
import { ModalDiscountFreeRangeImportComponent } from '../../../modals/parking/modal-discount-free-range-import/modal-discount-free-range-import.component';

@Component({
  selector: 'app-discount-free-range',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalDiscountFreeRangeComponent,
    ModalDiscountFreeRangeImportComponent,
  ],
  templateUrl: './discount-free-range.component.html',
  styleUrl: './discount-free-range.component.css',
})
export class DiscountFreeRangeComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiDiscountFreeRangeService);
  readonly mapper = inject(MapperDiscountFreeRangeService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly pageSizeOptions = [20, 50, 100] as const;

  items = signal<DiscountFreeRangeRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  searchText = '';
  filterEnabled: 'all' | 'on' | 'off' = 'all';
  /** yyyy-MM-dd — required, default last 7 days → today */
  startDate = '';
  endDate = '';

  confirmOpen = signal(false);
  confirmTarget: DiscountFreeRangeRow | null = null;
  confirmMode: 'toggle' | 'cancel' = 'toggle';

  constructor() {
    this.applyDefaultDates();
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        if (siteId) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  ngOnInit(): void {
    this.ensureDates();
    if (this.selectedSiteId) this.loadTable();
  }

  private toApiDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private applyDefaultDates(): void {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    this.startDate = this.toApiDate(start);
    this.endDate = this.toApiDate(end);
  }

  /** Dates must never be empty; start cannot be after end. */
  private ensureDates(): void {
    if (!this.startDate?.trim() || !this.endDate?.trim()) {
      this.applyDefaultDates();
      return;
    }
    if (this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
  }

  onDateChange(): void {
    this.ensureDates();
    this.currentPage.set(1);
    this.loadTable();
  }

  loadTable(): void {
    if (!this.selectedSiteId) return;
    this.ensureDates();
    this.isLoading.set(true);
    const isEnabled = this.filterEnabled === 'all' ? null : this.filterEnabled === 'on';
    this.api.getDiscountFreeRangeForTable({
      siteId: this.selectedSiteId,
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText.trim() || undefined,
      startDate: this.mapper.toYmd(this.startDate),
      endDate: this.mapper.toYmd(this.endDate),
      isEnabled,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res);
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages);
        this.currentPage.set(mapped.page);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลไม่สำเร็จ');
      },
    });
  }

  onSearchSubmit(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onPageSizeChange(size: number): void {
    this.isPageSizeOpen = false;
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(row: DiscountFreeRangeRow): void {
    this.selectedId.set(row.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
  }

  askToggle(row: DiscountFreeRangeRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!row.canEdit) {
      this.toast.error('ช่วงเวลายกเว้นเริ่มแล้ว ไม่สามารถแก้ไขได้');
      return;
    }
    this.confirmMode = 'toggle';
    this.confirmTarget = row;
    this.confirmOpen.set(true);
  }

  askCancel(row: DiscountFreeRangeRow): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ยกเลิก (ADMIN เท่านั้น)');
      return;
    }
    if (!row.isEnabled) {
      this.toast.error('รายการนี้ปิดใช้งานแล้ว ไม่สามารถยกเลิกซ้ำได้');
      return;
    }
    if (this.isExpired(row)) {
      this.toast.error('ช่วงยกเว้นหมดอายุแล้ว ไม่สามารถยกเลิกได้');
      return;
    }
    this.confirmMode = 'cancel';
    this.confirmTarget = row;
    this.confirmOpen.set(true);
  }

  /** ยกเลิกได้เฉพาะรายการที่ยังเปิดอยู่ และยังไม่หมดอายุ */
  canCancelRow(row: DiscountFreeRangeRow): boolean {
    return row.isEnabled && !this.isExpired(row);
  }

  cancelButtonTitle(row: DiscountFreeRangeRow): string {
    if (!row.isEnabled) return 'ปิดใช้งานแล้ว — ยกเลิกซ้ำไม่ได้';
    if (this.isExpired(row)) return 'หมดอายุแล้ว — ยกเลิกไม่ได้';
    return 'ยกเลิกรายการ';
  }

  /** หมดอายุ = วันเวลาสิ้นสุดผ่านเวลาปัจจุบันแล้ว (ตรงกับ API) */
  isExpired(row: DiscountFreeRangeRow): boolean {
    if (!row.endDate) return false;
    const end = new Date(row.endDate);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  }

  cancelToggle(): void {
    this.confirmOpen.set(false);
    this.confirmTarget = null;
  }

  confirmToggle(): void {
    const row = this.confirmTarget;
    const mode = this.confirmMode;
    this.confirmOpen.set(false);
    this.confirmTarget = null;
    if (!row) return;

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์');
      return;
    }

    this.isLoading.set(true);

    if (mode === 'cancel') {
      this.api.cancelDiscountFreeRange({ userId, menuId, id: row.id }).subscribe({
        next: (res) => {
          const mapped = this.mapper.mapActionResponse(res);
          this.isLoading.set(false);
          if (mapped.isSuccess) {
            this.toast.success(mapped.message || 'ยกเลิกสำเร็จ');
            this.loadTable();
            return;
          }
          this.toast.error(mapped.message || 'ยกเลิกไม่สำเร็จ');
        },
        error: () => {
          this.isLoading.set(false);
          this.toast.error('ยกเลิกไม่สำเร็จ');
        },
      });
      return;
    }

    this.api.updateDiscountFreeRangeStatus({
      userId,
      menuId,
      id: row.id,
      isEnabled: !row.isEnabled,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isLoading.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'เปลี่ยนสถานะสำเร็จ');
          this.loadTable();
          return;
        }
        this.toast.error(mapped.message || 'เปลี่ยนสถานะไม่สำเร็จ');
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('เปลี่ยนสถานะไม่สำเร็จ');
      },
    });
  }

  trackById(_: number, item: DiscountFreeRangeRow): number {
    return item.id;
  }

  // --- Import จากไฟล์ ---
  isImportOpen = signal(false);

  openImport(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    this.isImportOpen.set(true);
  }

  closeImport(): void {
    this.isImportOpen.set(false);
  }

  // --- ตัวกรองสถานะ (segmented) + page size dropdown ---
  readonly statusOptions: { value: 'all' | 'on' | 'off'; label: string }[] = [
    { value: 'all', label: 'ทั้งหมด' },
    { value: 'on', label: 'เปิดใช้งาน' },
    { value: 'off', label: 'ปิดใช้งาน' },
  ];
  isPageSizeOpen = false;

  setFilterEnabled(value: 'all' | 'on' | 'off'): void {
    if (this.filterEnabled === value) return;
    this.filterEnabled = value;
    this.onSearchSubmit();
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeOpen = !this.isPageSizeOpen;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isPageSizeOpen = false;
  }

  // --- ป้ายช่วงเวลา: รอเริ่ม / กำลังใช้งาน / หมดอายุ ---
  private rangePhase(row: DiscountFreeRangeRow): 'upcoming' | 'active' | 'expired' | 'unknown' {
    const start = row.startDate ? new Date(row.startDate).getTime() : NaN;
    const end = row.endDate ? new Date(row.endDate).getTime() : NaN;
    if (Number.isNaN(start) || Number.isNaN(end)) return 'unknown';
    const now = Date.now();
    if (now < start) return 'upcoming';
    if (now > end) return 'expired';
    return 'active';
  }

  rangePhaseLabel(row: DiscountFreeRangeRow): string {
    const phase = this.rangePhase(row);
    return phase === 'upcoming' ? 'รอเริ่ม' : phase === 'active' ? 'อยู่ในช่วง' : phase === 'expired' ? 'หมดอายุ' : '—';
  }

  rangePhaseClass(row: DiscountFreeRangeRow): string {
    const phase = this.rangePhase(row);
    if (phase === 'upcoming') return 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300';
    if (phase === 'active') return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300';
    if (phase === 'expired') return 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300';
    return 'bg-slate-50 text-slate-400';
  }
}
